import React, { useState, useEffect, useRef } from 'react';
import PageHeader from './components/PageHeader/PageHeader';
import ChallengeTabs from './components/ChallengeTabs/ChallengeTabs';
import ChallengeCard from './components/ChallengeCard/ChallengeCard';
import ChallengeListRow from './components/ChallengeListRow/ChallengeListRow';
import './ChallengesList.css';

function ChallengesList({ onNavigate, initialChallengeId, user }) {
  console.log('[ChallengesList] Component rendering, props:', { onNavigate: !!onNavigate, initialChallengeId, user: !!user });
  const [challenges, setChallenges] = useState([]);
  const [filter, setFilter] = useState('accepting');
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [counts, setCounts] = useState({ active: 0, accepting: 0, completed: 0 });
  const isInitialLoad = useRef(true); // Track initial load

  useEffect(() => {
    // Load view mode preference from localStorage
    const savedViewMode = localStorage.getItem('challenges_view_mode');
    if (savedViewMode && (savedViewMode === 'list' || savedViewMode === 'card')) {
      setViewMode(savedViewMode);
    }
    
    fetchChallenges();
    fetchCounts(); // Fetch counts on mount and when filter changes
    const interval = setInterval(() => {
      fetchChallenges();
      fetchCounts(); // Update counts periodically
    }, 30000);
    return () => {
      clearInterval(interval);
      isInitialLoad.current = true; // Reset for next visit
    };
  }, [filter]);

  useEffect(() => {
    // Save view mode preference to localStorage
    localStorage.setItem('challenges_view_mode', viewMode);
  }, [viewMode]);

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/backend/api/challenges/list.php?filter=${filter}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setChallenges(data.challenges);
          
          // Smart default: if current filter is empty and this is initial load, switch to first non-empty tab
          if ((data.challenges || []).length === 0 && filter === 'accepting' && isInitialLoad.current) {
            // Try active tab
            const activeResponse = await fetch('/backend/api/challenges/list.php?filter=active', {
              credentials: 'include'
            });
            
            if (activeResponse.ok) {
              const activeData = await activeResponse.json();
              if (activeData.success && activeData.challenges.length > 0) {
                setFilter('active');
                setChallenges(activeData.challenges);
              } else {
                // Try completed tab
                const completedResponse = await fetch('/backend/api/challenges/list.php?filter=completed', {
                  credentials: 'include'
                });
                
                if (completedResponse.ok) {
                  const completedData = await completedResponse.json();
                  if (completedData.success && completedData.challenges.length > 0) {
                    setFilter('completed');
                    setChallenges(completedData.challenges);
                  }
                }
              }
            }
            // Mark that initial load is complete
            isInitialLoad.current = false;
          }
        }
      }
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCounts = async () => {
    try {
      // Fetch counts for all statuses
      const [activeRes, acceptingRes, completedRes] = await Promise.all([
        fetch('/backend/api/challenges/list.php?filter=active', { credentials: 'include' }),
        fetch('/backend/api/challenges/list.php?filter=accepting', { credentials: 'include' }),
        fetch('/backend/api/challenges/list.php?filter=completed', { credentials: 'include' })
      ]);

      const [activeData, acceptingData, completedData] = await Promise.all([
        activeRes.json(),
        acceptingRes.json(),
        completedRes.json()
      ]);

      setCounts({
        active: activeData.success ? activeData.challenges.length : 0,
        accepting: acceptingData.success ? acceptingData.challenges.length : 0,
        completed: completedData.success ? completedData.challenges.length : 0
      });
    } catch (error) {
      console.error('Error fetching counts:', error);
    }
  };

  const handleJoinChallenge = async (challengeId) => {
    try {
      const response = await fetch('/backend/api/challenges/join.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId })
      });
      
      const data = await response.json();
      if (data.success) {
        fetchChallenges();
        fetchCounts(); // Add this line
      } else {
        alert(data.error || 'Failed to join challenge');
      }
    } catch (error) {
      console.error('Error joining challenge:', error);
      alert('Failed to join challenge');
    }
  };

  const handleEditChallenge = (challengeId) => {
    // TODO: Navigate to edit challenge modal
    console.log('Edit challenge:', challengeId);
  };

  const handleCancelChallenge = async (challengeId) => {
    if (!window.confirm('Are you sure you want to cancel this challenge? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch('/backend/api/challenges/cancel.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challengeId })
      });
      
      const data = await response.json();
      if (data.success) {
        fetchChallenges();
        fetchCounts(); // Add this line
        alert('Challenge cancelled successfully');
      } else {
        alert(data.error || 'Failed to cancel challenge');
      }
    } catch (error) {
      console.error('Error cancelling challenge:', error);
      alert('Failed to cancel challenge');
    }
  };

  const getTimeRemaining = (deadline) => {
    const now = new Date().getTime();
    const deadlineTime = new Date(deadline).getTime();
    const diff = deadlineTime - now;
    
    if (diff <= 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const formatEndTime = (endTime) => {
    if (!endTime) return '';
    const date = new Date(endTime);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Counts are now managed in state via fetchCounts()

  return (
    <div className="challenges-page">
      <PageHeader 
        onNavigate={onNavigate} 
        viewMode={viewMode} 
        onViewModeChange={setViewMode} 
      />
      
      <ChallengeTabs 
        activeFilter={filter} 
        onFilterChange={setFilter} 
        counts={counts}
      />
      
      {loading ? (
        <div className="challenges-loading">Loading challenges...</div>
      ) : challenges.length === 0 ? (
        <div className="challenges-empty">
          <p className="empty-icon">🏆</p>
          <p className="empty-title">No {filter} challenges</p>
          <p className="empty-subtitle">
            {filter === 'accepting' && 'Create a challenge to get started!'}
            {filter === 'active' && 'Join an open challenge or create your own!'}
            {filter === 'completed' && 'Completed challenges will appear here'}
          </p>
        </div>
      ) : (
        <>
          {viewMode === 'list' ? (
            <div className="challenges-list-view">
              {challenges.map(challenge => (
                <ChallengeListRow 
                  key={challenge.id}
                  challenge={challenge}
                  currentUserId={user?.id}
                  onJoin={handleJoinChallenge}
                  onViewDetails={(id) => onNavigate('challenge-detail', id)}
                  onEdit={handleEditChallenge}
                  onCancel={handleCancelChallenge}
                  getTimeRemaining={getTimeRemaining}
                  formatEndTime={formatEndTime}
                />
              ))}
            </div>
          ) : (
            <div className="challenges-grid-view">
              {challenges.map(challenge => (
                <ChallengeCard 
                  key={challenge.id}
                  challenge={challenge}
                  currentUserId={user?.id}
                  onJoin={handleJoinChallenge}
                  onViewDetails={(id) => onNavigate('challenge-detail', id)}
                  onEdit={handleEditChallenge}
                  onCancel={handleCancelChallenge}
                  getTimeRemaining={getTimeRemaining}
                  formatEndTime={formatEndTime}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChallengesList;

